import { useState, useEffect, useCallback, useRef } from 'react';
import type { SessionCoordinator } from '../SessionCoordinator';
import type { AppCoordinator } from '../../app/AppCoordinator';
import type { IElectronService } from '../../../services/electron/IElectronService';

export type StreamLifecycleResult = {
  isLive: boolean;
  isStarting: boolean;
  isStopping: boolean;
  isAuthenticated: boolean;
  buttonText: string;
  canUpdateTitle: boolean;
  startStream: () => void;
  stopStream: () => void;
  handleStartLoginStream: () => void;
  copyToClipboard: (text: string) => void;
};

type StreamLifecycleDeps = {
  sessionCoordinator: SessionCoordinator;
  appCoordinator: AppCoordinator;
  electronService: IElectronService;
  streamUrl: string;
  streamTitle: string;
  onSaveStreamTitle: () => void;
};

function mapStatus(status: string): { isLive: boolean; isStarting: boolean; isStopping: boolean; buttonText: string } {
  switch (status) {
    case 'hosting':
      return { isLive: true, isStarting: false, isStopping: false, buttonText: 'Stop Playcasting' };
    case 'busy':
      return { isLive: false, isStarting: true, isStopping: false, buttonText: 'Starting...' };
    default:
      return { isLive: false, isStarting: false, isStopping: false, buttonText: 'Start Playcasting' };
  }
}

export function useStreamLifecycle(deps: StreamLifecycleDeps): StreamLifecycleResult {
  const { sessionCoordinator, appCoordinator, electronService, streamUrl, streamTitle, onSaveStreamTitle } = deps;

  const [status, setStatus] = useState(sessionCoordinator.getStatus());
  const [isAuthenticated, setIsAuthenticated] = useState(appCoordinator.getAuthState().isAuthenticated);
  const autoStartTriggered = useRef(false);

  useEffect(() => {
    const unsubscribe = sessionCoordinator.events.statusChanged.addListener((e) => {
      setStatus(e.status);
    });
    return () => unsubscribe();
  }, [sessionCoordinator]);

  useEffect(() => {
    setIsAuthenticated(appCoordinator.getAuthState().isAuthenticated);
    const id = appCoordinator.addAuthListener((s) => setIsAuthenticated(s.isAuthenticated));
    return () => appCoordinator.removeAuthListener(id);
  }, [appCoordinator]);

  const mapped = mapStatus(status);

  useEffect(() => {
    if (mapped.isLive && streamUrl) {
      if (document.hasFocus()) {
        navigator.clipboard.writeText(streamUrl).catch(() => {});
      }
      electronService.copyToClipboard(streamUrl);
    }
  }, [mapped.isLive, streamUrl, electronService]);

  useEffect(() => {
    const waitingForLogin = localStorage.getItem('waitingForLogin');
    if (waitingForLogin && isAuthenticated && !autoStartTriggered.current) {
      autoStartTriggered.current = true;
      const savedTitle = localStorage.getItem('savedStreamTitle');
      localStorage.removeItem('waitingForLogin');
      localStorage.removeItem('savedStreamTitle');

      setTimeout(() => {
        if (savedTitle) onSaveStreamTitle();
        sessionCoordinator.startStream();
      }, 1000);
    }
  }, [isAuthenticated, sessionCoordinator, onSaveStreamTitle]);

  const startStream = useCallback(() => {
    onSaveStreamTitle();
    sessionCoordinator.startStream();
  }, [sessionCoordinator, onSaveStreamTitle]);

  const stopStream = useCallback(() => {
    sessionCoordinator.stopStream();
  }, [sessionCoordinator]);

  const handleStartLoginStream = useCallback(() => {
    if (!isAuthenticated) {
      if (electronService.isAvailable()) {
        localStorage.setItem('waitingForLogin', 'true');
        localStorage.setItem('savedStreamTitle', streamTitle);
        electronService.hostLogin();
      }
    } else {
      startStream();
    }
  }, [isAuthenticated, electronService, streamTitle, startStream]);

  const copyToClipboard = useCallback((text: string) => {
    if (document.hasFocus()) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
    electronService.copyToClipboard(text);
  }, [electronService]);

  return {
    isLive: mapped.isLive,
    isStarting: mapped.isStarting,
    isStopping: mapped.isStopping,
    isAuthenticated,
    buttonText: mapped.buttonText,
    canUpdateTitle: !mapped.isStarting && !mapped.isStopping,
    startStream,
    stopStream,
    handleStartLoginStream,
    copyToClipboard,
  };
}
